/**
 * Settings → Business Profile.
 *
 * THE SCREEN THAT MAKES THE OTHER NINE FEATURES POSSIBLE
 * -----------------------------------------------------
 * Every invoice prints the business's name, GSTIN, address and bank details,
 * and `businessReader` reads them from this row LIVE at print time. So a
 * business that has never filled this in produces invoices that are
 * unattributable, and a business that cannot correct a typo in its GSTIN is
 * stuck with it on every document it will ever issue. This is the first item in
 * the milestone because nothing else in the workflow is worth much without it.
 *
 * ONE FORM, TWO JOBS
 * ------------------
 * If no business exists this screen CREATES one; if one exists it EDITS it.
 * That is deliberate rather than a shortcut: a first-run user has no business,
 * so a screen that could only edit would leave the very first step of the
 * workflow unreachable.
 *
 * NO CALCULATION HAPPENS HERE
 * --------------------------
 * The form collects text and hands it to `businessService`, which owns the
 * validation. A second set of rules in the UI is a second answer to "is this
 * GSTIN valid", and the two drift — at which point the screen accepts something
 * the service refuses and the user is told "invalid" for no visible reason.
 * The service's error code is mapped to a field, and that mapping is the only
 * judgement made in this file.
 *
 * The form's own state is initialised ONCE, from the row, and re-seeded by
 * REMOUNTING rather than by an effect that copies a prop into state after the
 * first paint. That is not stylistic: the effect version renders an empty form
 * and then fills it in, so a user typing in the first second has their input
 * overwritten by a query that happened to resolve. The `key` below changes only
 * when the row itself changes — on load, and after this screen saves — which is
 * exactly when re-seeding is wanted and never while someone is mid-sentence.
 */
import { useRef, useState } from 'react';
import { Building2, Image as ImageIcon, Save, Trash2 } from 'lucide-react';
import { PageHeader } from '@ui/components/PageHeader';
import { Button } from '@ui/components/Button';
import { Field, Select, TextField, Textarea } from '@ui/components/Field';
import { LoadingState } from '@ui/components/States';
import { toast } from '@ui/components/Toaster';
import { BusinessServiceError, FY_START_MONTHS } from '@data/services/businessService';
import type { Business } from '@data/db/types';
import {
  STATE_OPTIONS,
  TERMS_MAX_LENGTH,
  emptyProfileForm,
  profileFormFromBusiness,
  profileInputFromForm,
  type ProfileFormState,
} from './businessProfileForm';
import { useBusinessProfile, useRemoveBusinessImage, useSaveBusinessProfile, useUploadBusinessImage } from './useBusinessProfile';
import { useAttachmentPreview } from './useAttachmentPreview';
import {
  ALLOWED_IMAGE_MIME,
  LOGO_MAX_BYTES,
  SIGNATURE_MAX_BYTES,
  formatBytes,
  labelFor,
  type BusinessImageKind,
} from '@data/services/attachmentService';

/**
 * Which field a refusal belongs to.
 *
 * Keyed by the service's code, so a rule added to the service lands here as an
 * unhighlighted field rather than as a message with no field attached. The
 * message always comes from the service, which knows the GSTIN that was typed.
 */
const FIELD_FOR_ERROR: Record<string, keyof ProfileFormState> = {
  NAME_REQUIRED: 'legalName',
  STATE_REQUIRED: 'stateCode',
  STATE_INVALID: 'stateCode',
  GSTIN_INVALID: 'gstin',
  GSTIN_STATE_MISMATCH: 'gstin',
  FY_START_INVALID: 'fyStartMonth',
};

export function BusinessProfileScreen() {
  const { loading, business, exists } = useBusinessProfile();

  if (loading) return <LoadingState label="Loading business profile…" />;

  return (
    <ProfileForm
      // The row's identity AND version: after this screen saves, the new version
      // remounts the form and the fields show what was actually stored, rather
      // than what was typed.
      key={`${business?.id ?? 'new'}:${business?.syncVersion ?? 0}`}
      business={business}
      exists={exists}
    />
  );
}

function ProfileForm({ business, exists }: { readonly business: Business | null; readonly exists: boolean }) {
  const save = useSaveBusinessProfile();

  // Lazy initialiser, so the row is read during the first render and the form is
  // never briefly empty.
  const [form, setForm] = useState<ProfileFormState>(() =>
    business ? profileFormFromBusiness(business) : emptyProfileForm(),
  );
  const [fieldError, setFieldError] = useState<{ field: keyof ProfileFormState; message: string } | null>(
    null,
  );

  const set = <K extends keyof ProfileFormState>(key: K, value: ProfileFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Clear a field's error as soon as the user edits it: an error that
    // survives an edit reads as "still wrong" about something they just fixed.
    setFieldError((current) => (current?.field === key ? null : current));
  };

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFieldError(null);
    try {
      const saved = await save.mutateAsync({
        businessId: business?.id ?? null,
        profile: profileInputFromForm(form),
      });
      toast({
        title: exists ? 'Business profile saved' : 'Business created',
        description: exists
          ? 'Invoices printed from now on will use these details.'
          : `${saved.legalName} is now the active company.`,
        tone: 'success',
      });
    } catch (error) {
      // A refusal with a known code belongs to a field; anything else (a
      // database failure, say) is a general failure and is reported as one.
      const code = error instanceof BusinessServiceError ? error.code : null;
      const message = error instanceof Error ? error.message : 'Could not save the business profile.';
      const field = code ? FIELD_FOR_ERROR[code] : undefined;
      if (field) setFieldError({ field, message });
      else toast({ title: 'Could not save the business profile', description: message, tone: 'danger' });
    }
  }

  const errorFor = (key: keyof ProfileFormState) => (fieldError?.field === key ? fieldError.message : undefined);

  /*
   * Logo and signature: written immediately, outside the profile's Save.
   *
   * The alternative - holding the file in form state until Save - means a
   * picked image is discarded by a navigate-away, and makes "remove" a mode the
   * form has to model. A file picker is a complete action; the bytes belong in
   * the database the moment the user chooses them.
   *
   * `attachmentId` is read from the STORED business, not from `form`: the
   * upload path never sets form state, so the form would always say "no logo"
   * and the preview would contradict what is actually printed.
   */
  const businessId = business?.id ?? null;

  const logoPreview = useAttachmentPreview(businessId, business?.logoAttachmentId);
  const signaturePreview = useAttachmentPreview(businessId, business?.signatureAttachmentId);

  const upload = useUploadBusinessImage();
  const remove = useRemoveBusinessImage();

  async function onPickImage(kind: BusinessImageKind, file: File) {
    if (!businessId) {
      // A logo for a company that does not exist yet. Create the company first,
      // then add the logo: an attachment row with no owner is unrecoverable.
      toast({
        title: 'Create the company first',
        description: 'Save this profile, then come back and add the image.',
        tone: 'danger',
      });
      return;
    }
    try {
      await upload.mutateAsync({ businessId, kind, file });
      toast({ title: `${labelFor(kind)} saved`, description: 'It will print from the next invoice.', tone: 'success' });
    } catch (error) {
      toast({
        title: `Could not save the ${labelFor(kind)}`,
        description: error instanceof Error ? error.message : 'The file could not be stored.',
        tone: 'danger',
      });
    }
  }

  async function onRemoveImage(kind: BusinessImageKind) {
    if (!businessId) return;
    try {
      await remove.mutateAsync({ businessId, kind });
      toast({ title: `${labelFor(kind)} removed`, tone: 'success' });
    } catch (error) {
      toast({
        title: `Could not remove the ${labelFor(kind)}`,
        description: error instanceof Error ? error.message : 'The image could not be removed.',
        tone: 'danger',
      });
    }
  }

  return (
    <form onSubmit={onSubmit} className="p-6">
      <PageHeader
        title="Business Profile"
        description={
          exists
            ? 'These details print on every invoice you issue. Changes apply from the next print.'
            : 'Set up your company once. These details print on every invoice you issue.'
        }
        action={
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            icon={<Save className="h-4 w-4" aria-hidden />}
          >
            {exists ? 'Save changes' : 'Create business'}
          </Button>
        }
      />

      <div className="flex max-w-3xl flex-col gap-6">
        <Section title="Identity" icon={<Building2 className="h-4 w-4" aria-hidden />}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Legal name"
              required
              value={form.legalName}
              error={errorFor('legalName')}
              onChange={(e) => set('legalName', e.target.value)}
              placeholder="As registered"
              hint="Exactly as on your GST registration."
            />
            <TextField
              label="Trade name"
              value={form.tradeName}
              onChange={(e) => set('tradeName', e.target.value)}
            />
            <TextField
              label="GSTIN"
              value={form.gstin}
              error={errorFor('gstin')}
              onChange={(e) => set('gstin', e.target.value.toUpperCase())}
              placeholder="36AAAAA1234A1Z5"
              className="font-mono"
            />
            <TextField
              label="PAN"
              value={form.pan}
              onChange={(e) => set('pan', e.target.value.toUpperCase())}
              className="font-mono"
            />
          </div>
        </Section>

        <Section title="Branding" icon={<ImageIcon className="h-4 w-4" aria-hidden />}>
          <p className="mb-3 text-sm text-slate-400">
            Printed on every invoice. Saved the moment you choose a file — no need to
            press Save changes afterwards.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <ImagePicker
              kind="LOGO"
              label="Logo"
              attachmentId={business?.logoAttachmentId}
              previewUrl={logoPreview.data}
              onPicked={onPickImage}
              onRemoved={onRemoveImage}
              busy={upload.isPending || remove.isPending}
            />
            <ImagePicker
              kind="SIGNATURE"
              label="Signature"
              attachmentId={business?.signatureAttachmentId}
              previewUrl={signaturePreview.data}
              onPicked={onPickImage}
              onRemoved={onRemoveImage}
              busy={upload.isPending || remove.isPending}
            />
          </div>
        </Section>

        <Section title="Registered state">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="State"
              required
              error={errorFor('stateCode')}
              hint="Decides whether invoices carry CGST + SGST or IGST."
            >
              {(wiring) => (
                <Select
                  {...wiring}
                  value={form.stateCode}
                  onChange={(e) => set('stateCode', e.target.value)}
                  options={STATE_OPTIONS}
                  placeholder="Choose a state"
                />
              )}
            </Field>
            <Field label="Financial year starts" hint="April for most Indian businesses.">
              {(wiring) => (
                <Select
                  {...wiring}
                  value={form.fyStartMonth}
                  onChange={(e) => set('fyStartMonth', e.target.value)}
                  options={FY_START_MONTHS.map((m) => ({ value: String(m.value), label: m.label }))}
                />
              )}
            </Field>
          </div>
        </Section>

        <Section title="Address">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Address" className="sm:col-span-2" hint="One line per row. City and pincode below.">
              {(wiring) => (
                <Textarea
                  {...wiring}
                  value={form.addressText}
                  onChange={(e) => set('addressText', e.target.value)}
                  rows={3}
                />
              )}
            </Field>
            <TextField label="City" value={form.city} onChange={(e) => set('city', e.target.value)} />
            <TextField label="District" value={form.district} onChange={(e) => set('district', e.target.value)} />
            <TextField
              label="Pincode"
              value={form.pincode}
              onChange={(e) => set('pincode', e.target.value)}
              inputMode="numeric"
            />
          </div>
        </Section>


        <Section title="Contact">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              inputMode="tel"
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
            <TextField
              label="Website"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
            />
            <TextField label="UPI ID" value={form.upiId} onChange={(e) => set('upiId', e.target.value)} />
          </div>
        </Section>

        <Section title="Invoice settings">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Default terms"
              className="sm:col-span-2"
              // Interpolated rather than typed out, so the sentence cannot claim a
              // limit the textarea does not actually enforce.
              hint={`Printed at the foot of every new invoice. Up to ${TERMS_MAX_LENGTH} characters.`}
            >
              {(wiring) => (
                <Textarea
                  {...wiring}
                  value={form.terms}
                  onChange={(e) => set('terms', e.target.value)}
                  // The cap is documentSchema's, not a guess. The stored row
                  // carries these terms, and a value past the schema's maximum
                  // would be a row that fails the next validation that reads it.
                  maxLength={TERMS_MAX_LENGTH}
                  rows={2}
                />
              )}
            </Field>
            <Checkbox
              label="Round invoice totals to the nearest rupee"
              checked={form.roundOffEnabled}
              onChange={(checked) => set('roundOffEnabled', checked)}
            />
            <Checkbox
              label="Print bank details on invoices"
              checked={form.showBankDetails}
              onChange={(checked) => set('showBankDetails', checked)}
            />
          </div>
        </Section>
      </div>
    </form>
  );
}

/**
 * One business image: preview, choose, remove.
 *
 * The file input is the control, not a hidden input plus a styled div. A real
 * `<input type="file">` gives keyboard access, a native file dialog, and the
 * right behaviour when the user cancels, none of which a re-implementation gets
 * right for free. It is visually a label because the styling lives there.
 *
 * `attachmentId` is the STORED id and is the source of truth for "is there an
 * image". It is not read from form state, because an upload does not go through
 * the form's Save - a preview that trusted form state would claim there is no
 * logo immediately after a successful upload.
 */
function ImagePicker({
  kind,
  label,
  attachmentId,
  previewUrl,
  onPicked,
  onRemoved,
  busy,
}: {
  readonly kind: BusinessImageKind;
  readonly label: string;
  readonly attachmentId?: string;
  readonly previewUrl?: string;
  readonly onPicked: (kind: BusinessImageKind, file: File) => void;
  readonly onRemoved: (kind: BusinessImageKind) => void;
  readonly busy: boolean;
}) {
  // A native file input cannot be reset by clearing state: picking the SAME file
  // twice in a row fires no change event unless the value is cleared. Without
  // this, "I picked the wrong logo, let me pick the right one" silently does
  // nothing - and the user has no way to tell that from the app being broken.
  const inputRef = useRef<HTMLInputElement>(null);

  const has = Boolean(attachmentId);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-slate-200">{label}</span>

      <div className="flex items-center gap-3">
        <div className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-800 bg-slate-950">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`${label} preview`}
              className="h-full w-full object-contain"
              data-testid={`${kind.toLowerCase()}-preview`}
            />
          ) : (
            <span className="px-2 text-center text-xs text-slate-600">No {label.toLowerCase()}</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          {/*
            `hidden` rather than `sr-only`: a visually hidden input is still in
            the tab order, so a keyboard user would tab into a control they
            cannot see focus on. The LABEL is the focusable control instead.
          */}
          <input
            ref={inputRef}
            id={`${kind.toLowerCase()}-input`}
            type="file"
            accept={ALLOWED_IMAGE_MIME.join(',')}
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Cleared immediately, unconditionally - including when there is
              // no file, because "cancel" also has to leave it re-pickable.
              e.target.value = '';
              if (file) onPicked(kind, file);
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={busy}
            loadingText="Saving…"
            onClick={() => inputRef.current?.click()}
            data-testid={`${kind.toLowerCase()}-choose`}
          >
            {has ? `Replace ${label.toLowerCase()}` : `Choose ${label.toLowerCase()}`}
          </Button>
          {has && (
            <Button
              type="button"
              size="sm"
              variant="danger"
              disabled={busy}
              icon={<Trash2 className="h-3.5 w-3.5" aria-hidden />}
              onClick={() => onRemoved(kind)}
              data-testid={`${kind.toLowerCase()}-remove`}
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-500">
        PNG, JPEG or Webp, up to{' '}
        {formatBytes(kind === 'LOGO' ? LOGO_MAX_BYTES : SIGNATURE_MAX_BYTES)}. Printed at a
        fixed size, so the image's own dimensions cannot change your page breaks.
      </p>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  readonly title: string;
  readonly icon?: React.ReactNode;
  readonly children: React.ReactNode;
}) {
  return (
    // A real heading, so a screen reader can jump between the groups instead of
    // reading the form as one undifferentiated wall of inputs.
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-300">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
      {label}
    </label>
  );
}

